// A small XML tree (`XMLNodeTree`, SuiteImport): elements, attributes and text, with entities, CDATA and comments, no
// DTDs or namespaces — enough for Scrivener's `.scrivx`, `content.comments` and snapshot indexes, the same in a
// browser and in Node (which has no DOMParser). Deterministic, so a parity test can hold the Mac and the web to one
// reading of a binder.

export class XMLNode {
  children: XMLNode[] = [];
  text = '';

  constructor(readonly name: string, readonly attributes: Record<string, string>) {}

  child(name: string): XMLNode | undefined { return this.children.find((c) => c.name === name); }
  childrenNamed(name: string): XMLNode[] { return this.children.filter((c) => c.name === name); }
  attr(name: string): string | undefined { return this.attributes[name]; }
  /** Trimmed text of the named child. */
  value(name: string): string | undefined { return this.child(name)?.text.trim(); }
}

export class XMLError extends Error {}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (whole, body: string) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code >= 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[body] ?? whole;
  });
}

/** Parses a document (UTF-8 bytes or text) into its root element. */
export function parseXML(source: Uint8Array | string): XMLNode {
  const text = typeof source === 'string' ? source : new TextDecoder('utf-8').decode(source);
  const root = new XMLNode('#document', {});
  const stack: XMLNode[] = [root];
  let i = 0;
  const n = text.length;
  while (i < n) {
    const lt = text.indexOf('<', i);
    if (lt < 0) { stack[stack.length - 1].text += decodeEntities(text.slice(i)); break; }
    if (lt > i) stack[stack.length - 1].text += decodeEntities(text.slice(i, lt));
    if (text.startsWith('<!--', lt)) {
      const end = text.indexOf('-->', lt + 4);
      if (end < 0) throw new XMLError('Unterminated comment');
      i = end + 3;
      continue;
    }
    if (text.startsWith('<![CDATA[', lt)) {
      const end = text.indexOf(']]>', lt + 9);
      if (end < 0) throw new XMLError('Unterminated CDATA');
      stack[stack.length - 1].text += text.slice(lt + 9, end);
      i = end + 3;
      continue;
    }
    if (text.startsWith('<?', lt) || text.startsWith('<!', lt)) {
      const end = text.indexOf('>', lt);
      if (end < 0) throw new XMLError('Unterminated declaration');
      i = end + 1;
      continue;
    }
    const end = closingAngle(text, lt);
    if (end < 0) throw new XMLError('Unterminated tag');
    const inner = text.slice(lt + 1, end);
    i = end + 1;
    if (inner.startsWith('/')) {
      const name = inner.slice(1).trim();
      if (stack.length < 2 || stack[stack.length - 1].name !== name) throw new XMLError(`Unexpected </${name}>`);
      stack.pop();
      continue;
    }
    const selfClosing = inner.endsWith('/');
    const body = selfClosing ? inner.slice(0, -1) : inner;
    const nameEnd = body.search(/[\s/]|$/);
    const name = body.slice(0, nameEnd);
    if (!name) throw new XMLError('Empty tag name');
    const attributes: Record<string, string> = {};
    const attrPattern = /([^\s=]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
    for (const m of body.slice(nameEnd).matchAll(attrPattern)) attributes[m[1]] = decodeEntities(m[2] ?? m[3] ?? '');
    const node = new XMLNode(name, attributes);
    stack[stack.length - 1].children.push(node);
    if (!selfClosing) stack.push(node);
  }
  if (stack.length !== 1) throw new XMLError(`Unclosed <${stack[stack.length - 1].name}>`);
  const element = root.children[0];
  if (!element) throw new XMLError('No root element');
  return element;
}

/** The `>` ending a tag, skipping any inside quoted attribute values. */
function closingAngle(text: string, from: number): number {
  let quote: string | null = null;
  for (let k = from + 1; k < text.length; k++) {
    const c = text[k];
    if (quote) { if (c === quote) quote = null; continue; }
    if (c === '"' || c === "'") quote = c;
    else if (c === '>') return k;
  }
  return -1;
}
